import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared Footer Component
 * Used across all pages for consistent branding and navigation
 *
 * Footer text is dynamically loaded from admin_settings table
 *
 * @param {Object} props
 * @param {boolean} props.minimal - If true, hides the navigation grid (logo, links, etc.)
 */
function Footer({ minimal = false }) {
  const [footerText, setFooterText] = useState('© 2025 Daring Futures Ltd | We surface and resolve hidden work'); // Default fallback

  useEffect(() => {
    // Fetch footer text from settings API
    const fetchFooterText = async () => {
      try {
        const response = await fetch('/api/admin/settings', {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          const textSetting = data.settings?.find(s => s.settingKey === 'footer_text');
          if (textSetting?.settingValue) {
            setFooterText(textSetting.settingValue);
          }
        }
      } catch (error) {
        console.error('Failed to fetch footer text:', error);
        // Keep default fallback text
      }
    };

    fetchFooterText();
  }, []);
  return (
    <footer className="bg-[#EAEFF5] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {!minimal && (
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Tagline */}
            <div>
              <div className="mb-4">
                <img
                  src={process.env.PUBLIC_URL + '/df-web-logo.svg'}
                  alt="Daring Futures - Realising Ambition"
                  className="h-12 border border-[#EAEFF5] rounded"
                />
              </div>
              <p className="text-sm text-[#697A90] italic">
                Operational truth-telling for transformation leaders
              </p>
            </div>

            {/* Connect */}
            <div>
              <h3 className="font-poppins font-bold text-[#062B49] mb-4">Connect</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://www.linkedin.com/company/daringfutures"
                    className="text-[#697A90] hover:text-[#86C2CA] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="https://twitter.com/daringfutures"
                    className="text-[#697A90] hover:text-[#86C2CA] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Twitter
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="font-poppins font-bold text-[#062B49] mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://daringfutures.com/wiki"
                    className="text-[#697A90] hover:text-[#86C2CA] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Daring Wiki
                  </a>
                </li>
                <li>
                  <Link
                    to="/request-diagnostic"
                    className="text-[#697A90] hover:text-[#86C2CA] transition-colors"
                  >
                    Mini Diagnostic
                  </Link>
                </li>
              </ul>
            </div>

            {/* Who We Help */}
            <div>
              <h3 className="font-poppins font-bold text-[#062B49] mb-4">Who We Help</h3>
              <ul className="space-y-2 text-sm">
                <li className="text-[#697A90]">Transformation Leaders</li>
                <li className="text-[#697A90]">Senior Leaders</li>
                <li className="text-[#697A90]">Agile Coaches</li>
              </ul>
            </div>
          </div>
        )}

        {/* Copyright & Legal Links */}
        <div className={`${!minimal ? 'border-t border-[#B3C9D3]' : ''} pt-8 text-center text-sm text-[#697A90]`}>
          {minimal && (
            <p className="mb-4">
              Truth Tellers is a powerful collaboration between{' '}
              <a
                href="https://www.daringfutures.com"
                className="text-[#86C2CA] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Daring Futures
              </a>
              {' '}and{' '}
              <a
                href="https://www.unlikebefore.com"
                className="text-[#86C2CA] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Unlike Before
              </a>
            </p>
          )}
          <p>{footerText}</p>
          <p className="mt-2">
            <a
              href="https://wiki.daringfutures.com/Working+With+Us/Service+Delivery+Policies/Policy+-+Terms+and+Conditions"
              className="hover:text-[#86C2CA] transition-colors ml-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </a> |
            <a
              href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies"
              className="hover:text-[#86C2CA] transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}Privacy & Cookie Policy
            </a> |
            <a
              href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Licensing+and+Fair+Use"
              className="hover:text-[#86C2CA] transition-colors ml-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Fair Use Policy
            </a> |
            <a
              href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Anti-Slavery+Statement"
              className="hover:text-[#86C2CA] transition-colors ml-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Anti-Slavery Statement
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
